import {users as usersShared} from '@cl/shared';
import iconsObj from 'common/src/assets/icons/iconsObj';
import {EntityTypeToRename} from 'common/src/modules/shared/copilot/copilot.enums';
import {AgentContext} from 'common/src/modules/shared/copilot/copilot.types';
import creativeTemplatesLibraryContext from 'common/src/modules/creativeTemplates/copilot/context/creativeTemplatesLibraryContext';
import {doesUserHaveRole} from 'common/src/modules/roles/utils/doesUserHaveRole';
import {AgentName} from 'common/src/modules/shared/copilot/copilot.enums';
import {Agent, ConversationStarter} from 'common/src/modules/shared/copilot/copilot.types';
import batchRenameEntitiesTool from 'common/src/modules/shared/copilot/tools/batchRenameEntitiesTool';
import agent from 'common/src/modules/shared/copilot/utils/creators/agent';
import {RoleTypeName} from 'common/src/modules/users/users.types';
import {CLStore} from 'common/src/redux/store';
import addRandomKeyToObjectArray from 'common/src/utils/arrayUtils/addRandomKeyToObjectArray';
import creativeTemplatePageContext from '../../context/creativeTemplatePageContext';
import navigateToCreativeTemplateBuildOrMapTabTool from '../../tools/navigateToCreativeTemplateBuildOrMapTabTool';
import {CreativeTemplatesManagerAgentToolName} from './creativeTemplatesManagetAgent.enums';
import createCreativeTemplateTool from './tools/createCreativeTemplateTool';
import duplicateCreativeTemplateTool from './tools/duplicateCreativeTemplateTool';
import filterCreativeTemplatesLibraryTool from './tools/filterCreativeTemplatesLibraryTool';
import getCreativeTemplateInfoTool from './tools/getCreativeTemplateInfoTool';
import getTemplatesIdsByNameTool from './tools/getTemplatesIdsByNameTool';
import navigateToCreativeTemplateHomeOrSetOrTearsheetTabTool from './tools/navigateToCreativeTemplateHomeOrSetOrTearsheetTabTool';
import navigateToCreativeTemplatesLibraryTool from './tools/navigateToCreativeTemplatesLibraryTool';
import showCreativeTemplatePreviewTool from './tools/showCreativeTemplatePreviewTool';

function creativeTemplatesManagerAgent(store: CLStore): Agent {
  const userHasTemplateWriteRole = doesUserHaveRole(
    usersShared.userSubject.data,
    RoleTypeName.CreativeTemplatesWrite,
  );

  const role = 'You are the agent responsible for managing creative templates.';

  const inScope = ['Find, navigate and manage creative templates'];

  const instructions = `
  - When number is provided for a template, this is mostly a template ID and not template name. 
  - If user asks his own templates using terms like 'my' or 'mine', this means the user refers to the current user from the user context.
  - If you dont find ID in a list you found earlier, just use the id provided by the user for the rest of the tools.
  - When create new template, ask user for inputs one at a time except features.
  - If you need to filter templates, *must* navigate to creative templates library first.
  - Before you delegate to the adbuilder agent, you must know the template id and navigate to the build tab of the template first.
  - If you need template format, ask user for format according to existing formats or choose the first one. **NEVER** guess the format or give 0x0 format.
`;

  const permissionsRelatedTools = userHasTemplateWriteRole
    ? [
        createCreativeTemplateTool(),
        duplicateCreativeTemplateTool(),
        batchRenameEntitiesTool({
          entityType: EntityTypeToRename.Template,
          toolName: CreativeTemplatesManagerAgentToolName.BatchRenameTemplates,
        }),
      ]
    : [];

  const tools = [
    ...permissionsRelatedTools,
    navigateToCreativeTemplatesLibraryTool(store),
    navigateToCreativeTemplateBuildOrMapTabTool(store),
    navigateToCreativeTemplateHomeOrSetOrTearsheetTabTool(),
    getTemplatesIdsByNameTool(),
    getCreativeTemplateInfoTool(store),
    showCreativeTemplatePreviewTool(store),
    filterCreativeTemplatesLibraryTool(store),
  ];

  const contexts: AgentContext[] = [
    creativeTemplatesLibraryContext(store),
    creativeTemplatePageContext(store),
  ];

  const conversationStarters: ConversationStarter[] = addRandomKeyToObjectArray([
    {
      displayName: 'Navigate to Template',
      prompt: 'Navigate to Template named: ',
      icon: {
        src: iconsObj.preview,
        color: '#4A90E2',
      },
    },
    {
      displayName: 'Create New Template',
      prompt: 'Create a new creative template named: ',
      icon: {
        src: iconsObj.edit,
        color: '#50C878',
      },
    },
    {
      displayName: 'Filter Templates',
      prompt: 'Filter templates that',
      icon: {
        src: iconsObj.dynamicParams.paramTypeDefault,
        color: '#FF6B35',
      },
    },
    {
      displayName: 'Bulk Rename Templates',
      prompt: 'Bulk rename selected templates',
      icon: {
        src: iconsObj.edit,
        color: '#9B59B6',
      },
    },
  ]);

  return agent({
    name: AgentName.CreativeTemplatesManager,
    role,
    store,
    instructions,
    tools,
    inScope,
    contexts,
    conversationStarters,
  });
}

export default creativeTemplatesManagerAgent;
